import styled from '@emotion/styled'
import { borderRadius, colors, spacing } from '@retailer-platform/shared-components'
import moment, { type Moment } from 'moment'
import React, { type FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react'
import { DateRangePicker, type FocusedInputShape } from 'react-dates'
import { Text } from '@retailer-platform/shared-components'
import { css, Global } from '@emotion/react'
import { useDomainMessages } from 'domains/retailer-collections/utils/domain/intl'

export type DateRange = {
  startDate?: Date
  endDate?: Date
}

interface RetailerCollectionsFormDatepickerProps {
  value: DateRange
  onChange: (nextValue: DateRange) => void
  disabled?: boolean
}

const reactDatesGlobalStyles = css({
  '.DateRangePicker_picker__portal': {
    zIndex: 101,
  },
})

const Wrapper = styled.div({
  display: 'flex',
  flexDirection: 'row',
  gap: spacing.X12,
  alignItems: 'center',

  '&&': {
    '.DateRangePickerInput': {
      border: `1px solid ${colors.GRAYSCALE.X30}`,
      borderRadius: borderRadius.X8,
      height: 'auto',
    },

    '.DateInput': {
      background: 'transparent',
    },
    '.DateInput_input': {
      background: 'transparent',
      padding: `${spacing.X8} ${spacing.X12}`,
      lineHeight: '18px',
      margin: 0,
    },

    '.DateInput_input__focused': {
      borderBottom: `2px solid ${colors.GRAYSCALE.X70}`,
    },

    '#startDate': {
      borderBottomRightRadius: 0,
    },

    '#endDate': {
      borderBottomLeftRadius: 0,
    },

    '.DateRangePickerInput_arrow': {
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1,
    },

    '.DateRangePickerInput_arrow_svg': {
      height: 16,
      width: 16,
    },

    '.DateRangePickerInput, .SingleDatePickerInput, .DateInput, .DateInput_input': {
      fontSize: 15,
    },
  },
})

export const RetailerCollectionsFormDatepicker: FunctionComponent<
  RetailerCollectionsFormDatepickerProps
> = ({ value, onChange, disabled }) => {
  const [focusedInput, setFocusedState] = useState<FocusedInputShape | null>(null)
  const messages = useDomainMessages({
    clearAll: 'retailerCollectionsDomain.form.datePicker.clearAll',
  })

  // Datepicker works with moment dates exclusively.
  const momentDates = useMemo(() => {
    const [startDate, endDate] = [value.startDate, value.endDate].map(date =>
      date ? moment(date) : null
    )

    return { startDate, endDate }
  }, [value.endDate, value.startDate])

  const handleOnChange = useCallback(
    (args: { startDate?: Moment; endDate?: Moment }) =>
      onChange?.({
        startDate: args.startDate?.toDate(),
        endDate: args.endDate?.toDate(),
      }),
    [onChange]
  )

  return (
    <Wrapper>
      <Global styles={reactDatesGlobalStyles} />

      <DateRangePicker
        minimumNights={0}
        startDate={momentDates.startDate}
        startDateId="startDate"
        endDate={momentDates.endDate}
        endDateId="endDate"
        onDatesChange={handleOnChange}
        focusedInput={focusedInput}
        onFocusChange={setFocusedState}
        withPortal
        disabled={disabled}
      />

      {value.startDate && (
        <Text
          variant="link"
          size="small"
          weight="semibold"
          onClick={() => onChange({ startDate: null, endDate: null })}
        >
          {messages.clearAll}
        </Text>
      )}
    </Wrapper>
  )
}
